import React, { useRef, useState } from "react";
import { Image as ImageIcon, Film, Plus, Loader2, Images } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "./ui/dropdown-menu";
import { toast } from "sonner";
import api from "../lib/api";
import { measureAny, extractVideoThumbnail } from "../lib/measure";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const absUrl = (u) => (u && u.startsWith("http") ? u : `${BACKEND}${u || ""}`);

export default function Toolbar({ onAdd, onUploadDone }) {
  const fileImg = useRef(null);
  const fileVid = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState("");

  const notifyDone = () => {
    if (typeof onUploadDone === "function") onUploadDone();
  };

  const handleImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setProgress(0);
    try {
      const uploaded = [];
      for (let i = 0; i < files.length; i++) {
        setStage(`Uploading ${i + 1} / ${files.length}`);
        const data = await api.uploads.upload(files[i], setProgress);
        uploaded.push({ url: absUrl(data.url), name: files[i].name });
      }
      // Build a single card. Primary src = first item; rest go in gallery.
      const primary = uploaded[0];
      const dim = await measureAny("image", primary.url);
      const gallery = uploaded.slice(1).map((u) => ({
        type: "image",
        src: u.url,
        thumbnail: "",
      }));
      onAdd({
        type: "image",
        category: "graphic",
        src: primary.url,
        thumbnail: "",
        title: primary.name.replace(/\.[^.]+$/, ""),
        width: dim.width,
        height: dim.height,
        gallery,
      });
      toast(
        uploaded.length > 1
          ? `Card created with ${uploaded.length} images`
          : "Image uploaded",
      );
      notifyDone();
    } catch (err) {
      toast(err?.response?.status === 413 ? "File too large" : "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
      setStage("");
      e.target.value = "";
    }
  };

  const handleVideo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    setProgress(0);
    setStage("Uploading video");
    try {
      // Try to grab a thumbnail frame from the video file BEFORE upload, so
      // the canvas card has a poster image and doesn't try to load the entire
      // video on first paint.
      let thumbnailUrl = "";
      try {
        const thumbBlob = await extractVideoThumbnail(f);
        if (thumbBlob) {
          setStage("Saving thumbnail");
          const thumbFile = new File([thumbBlob], `${f.name}.thumb.jpg`, {
            type: "image/jpeg",
          });
          const thumbData = await api.uploads.upload(thumbFile);
          thumbnailUrl = absUrl(thumbData.url);
        }
      } catch {
        // best-effort — proceed without thumbnail
      }

      setStage("Uploading video");
      const data = await api.uploads.upload(f, setProgress);
      const url = absUrl(data.url);
      const dim = await measureAny("video", url);
      onAdd({
        type: "video",
        category: "video",
        src: url,
        thumbnail: thumbnailUrl,
        title: f.name.replace(/\.[^.]+$/, ""),
        width: dim.width,
        height: dim.height,
      });
      toast("Video uploaded");
      notifyDone();
    } catch (err) {
      toast(err?.response?.status === 413 ? "File too large" : "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
      setStage("");
      e.target.value = "";
    }
  };

  const handleUrl = async (type) => {
    const url = prompt(type === "video" ? "Video URL (mp4)" : "Image URL");
    if (!url) return;
    const dim = await measureAny(type, url);
    onAdd({
      type,
      category: type === "video" ? "video" : "graphic",
      src: url,
      width: dim.width,
      height: dim.height,
    });
  };

  return (
    <div className="flex items-center gap-2">
      <input
        ref={fileImg}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={handleImages}
      />
      <input
        ref={fileVid}
        type="file"
        accept="video/*"
        hidden
        onChange={handleVideo}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            disabled={uploading}
            className="rounded-full bg-[var(--vermillion)] hover:bg-[var(--vermillion-soft)] text-white btn-pop pl-3 pr-4 h-10"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                {stage} {progress}%
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-1" /> Add card
              </>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" className="w-64">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] text-ink-soft">
            Upload
          </DropdownMenuLabel>
          <DropdownMenuItem onClick={() => fileImg.current?.click()}>
            <Images className="w-4 h-4 mr-2" /> Images from device
            <span className="ml-auto text-[10px] text-ink-soft/60">multi</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => fileVid.current?.click()}>
            <Film className="w-4 h-4 mr-2" /> Video from device
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] text-ink-soft">
            From URL
          </DropdownMenuLabel>
          <DropdownMenuItem onClick={() => handleUrl("image")}>
            <ImageIcon className="w-4 h-4 mr-2" /> Image URL
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleUrl("video")}>
            <Film className="w-4 h-4 mr-2" /> Video URL
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
